import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";
import { trackDemoComplete } from "@/lib/analytics";
import ScoreGauge from "@/components/ScoreGauge";
import { RiskBadge, ClassBadge, scoreColor } from "@/lib/status";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, DownloadSimple, CaretRight, Sparkle, WarningCircle, Lightning, ListChecks } from "@phosphor-icons/react";

const SUBSCORE_LABELS = {
  accuracy: "Accuracy", profitability: "Profitability", labor_allocation: "Labor Allocation",
  scope_completeness: "Scope Completeness", equipment_selection: "Equipment Selection",
  risk: "Risk", market_position: "Market Position", professional_presentation: "Professional Presentation",
};

const Card = ({ title, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testId}>
    {title && <h2 className="font-display font-bold text-lg text-[#0F172A] mb-4">{title}</h2>}
    {children}
  </div>
);

function Bar({ label, score }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="font-display font-bold text-sm" style={{ color: scoreColor(score) }}>{score}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: scoreColor(score) }} />
      </div>
    </div>
  );
}

function ListCard({ title, items, accent, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <Card title={title} testId={testId}>
      <ul className="space-y-2">
        {items.map((it, idx) => (
          <li key={idx} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
            <CaretRight size={14} weight="bold" className="mt-1 shrink-0" style={{ color: accent || "#475569" }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tick, setTick] = useState(0);
  const [packages, setPackages] = useState([]);
  const [buying, setBuying] = useState(false);
  const [decisions, setDecisions] = useState({});

  useEffect(() => { apiClient.get("/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (p?.rewrite_status === "changes_ready" && Array.isArray(p.proposed_changes)) {
      const approved = new Set(p.approved_change_ids || p.proposed_changes.filter((c) => c.recommended).map((c) => c.id));
      setDecisions(Object.fromEntries(p.proposed_changes.map((c) => [c.id, approved.has(c.id)])));
    }
  }, [p?.rewrite_status, p?.proposed_changes, p?.approved_change_ids]);

  useEffect(() => {
    let timer, cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await apiClient.get(`/proposals/${id}`);
        if (cancelled) return;
        setP(r.data);
        // demo_complete: fire once when a demo analysis finishes successfully (deduped per proposal)
        if (r.data.is_demo_analysis && r.data.review_status === "complete") trackDemoComplete(id);
        if (r.data.review_status === "processing" || ["generating", "proposing"].includes(r.data.rewrite_status)) timer = setTimeout(fetchOnce, 3000);
      } catch { if (!cancelled) setNotFound(true); }
    };
    fetchOnce();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id, tick]);

  const startCheckout = async (lookup_key) => {
    setBuying(true);
    try {
      const r = await apiClient.post("/payments/checkout", { lookup_key, proposal_id: id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Could not start checkout"); setBuying(false); }
  };
  const proposeChanges = async () => {
    try { await apiClient.post(`/proposals/${id}/propose-changes`); setP((x) => ({ ...x, rewrite_status: "proposing" })); setTick((n) => n + 1); }
    catch { toast.error("Could not plan changes"); }
  };
  const generateWithDecisions = async () => {
    const approved_change_ids = Object.entries(decisions).filter(([, v]) => v).map(([k]) => k);
    try { await apiClient.post(`/proposals/${id}/rewrite`, { approved_change_ids }); setP((x) => ({ ...x, rewrite_status: "generating" })); setTick((n) => n + 1); }
    catch { toast.error("Could not start rewrite"); }
  };
  const generateRewrite = async () => {
    try { await apiClient.post(`/proposals/${id}/rewrite`); setP((x) => ({ ...x, rewrite_status: "generating" })); setTick((n) => n + 1); }
    catch { toast.error("Could not start rewrite"); }
  };
  const markOutcome = async (outcome) => {
    const prev = p.outcome;
    setP((x) => ({ ...x, outcome }));
    try {
      await apiClient.patch(`/proposals/${id}/outcome`, { outcome });
      toast.success(outcome === "won" ? "Marked as won 🎉" : outcome === "lost" ? "Marked as lost" : "Set to pending");
    } catch { setP((x) => ({ ...x, outcome: prev })); toast.error("Could not update outcome"); }
  };
  const downloadRewritePdf = async () => {
    try {
      const res = await apiClient.get(`/proposals/${id}/rewrite-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `ProposalForensics_Proposal_${(p?.project_name || "proposal").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download proposal"); }
  };

  const retry = async () => {
    try { await apiClient.post(`/proposals/${id}/retry`); setP((x) => ({ ...x, review_status: "processing" })); setTick((n) => n + 1); } catch { /* noop */ }
  };
  const downloadPdf = async () => {
    try {
      const res = await apiClient.get(`/proposals/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ProposalForensics_Review_${(p?.project_name || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the PDF report"); }
  };

  if (notFound) return <div className="text-slate-500">Proposal not found.</div>;
  if (!p) return <div className="text-slate-400 animate-pulse">Loading analysis…</div>;

  if (p.review_status === "processing") {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="review-processing">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#0F172A] mb-6"><Sparkle size={26} weight="fill" color="#fff" className="animate-pulse" /></div>
        <h1 className="font-display font-black text-3xl tracking-tighter text-[#0F172A]">Analyzing proposal…</h1>
        <p className="text-slate-500 mt-3">Proposal Forensics is auditing <span className="font-semibold text-[#0F172A]">{p.project_name}</span> — financials, labor, scope, equipment, risk and market position against your standards &amp; regional benchmarks. This usually takes 1–2 minutes.</p>
        <div className="mt-8 h-1.5 w-full max-w-sm mx-auto rounded-full bg-slate-200 overflow-hidden"><div className="h-full w-1/3 bg-[#0F172A] rounded-full animate-pulse" /></div>
        <button onClick={() => navigate("/")} className="mt-8 text-sm text-slate-500 hover:text-[#0F172A] transition-colors">Back to dashboard — it'll be ready when you return</button>
      </div>
    );
  }
  if (p.review_status === "failed") {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="review-failed">
        <h1 className="font-display font-black text-3xl tracking-tighter text-[#0F172A]">Analysis didn't complete</h1>
        <p className="text-slate-500 mt-3">Proposal Forensics hit an error and didn't finish. You can run it again.</p>
        {p.error && <p className="text-xs text-slate-400 mt-2 font-mono-plex">{p.error}</p>}
        <div className="mt-8 flex items-center justify-center gap-3">
          <button onClick={() => navigate("/")} className="h-11 px-5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Dashboard</button>
          <button onClick={retry} data-testid="retry-review-btn" className="inline-flex items-center gap-2 h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors"><Sparkle size={16} weight="fill" /> Retry Analysis</button>
        </div>
      </div>
    );
  }

  const a = p.analysis || {};
  const ss = a.subscores || {};
  const fin = a.financial || {};
  const lab = a.labor || {};
  const issues = (a.accuracy_audit || []).length + (a.missing_scope || []).length;
  const opps = (a.recommendations || []).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0F172A] transition-colors"><ArrowLeft size={16} weight="bold" /> Dashboard</button>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5" data-testid="outcome-control">
            <span className="px-2 font-mono-plex text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-400">Deal</span>
            {[["pending", "Pending"], ["won", "Won"], ["lost", "Lost"]].map(([val, label]) => {
              const active = (p.outcome || "pending") === val;
              const activeCls = val === "won" ? "bg-emerald-600 text-white" : val === "lost" ? "bg-red-600 text-white" : "bg-slate-800 text-white";
              return (
                <button key={val} data-testid={`outcome-${val}`} onClick={() => markOutcome(val)}
                  className={`px-3 h-8 rounded text-xs font-semibold transition-colors ${active ? activeCls : "text-slate-500 hover:bg-slate-100"}`}>{label}</button>
              );
            })}
          </div>
          <button data-testid="download-pdf-btn" onClick={downloadPdf} className="inline-flex items-center gap-2 h-10 px-4 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors"><DownloadSimple size={16} weight="bold" /> Download PDF</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card testId="result-header" title={null}>
          <h1 className="font-display font-black text-2xl tracking-tighter text-[#0F172A]" data-testid="result-project-name">{p.project_name}</h1>
          <p className="text-slate-500 mt-1 text-sm">{p.client_name} · {p.project_type}</p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Value</div><div className="font-display font-bold text-lg text-[#0F172A] mt-1">${(Number(p.proposal_value) || 0).toLocaleString()}</div></div>
            <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Gross Margin</div><div className="font-display font-bold text-lg text-[#0F172A] mt-1 line-clamp-2">{p.gross_margin}</div></div>
            <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Risk Level</div><div className="mt-1.5"><RiskBadge value={p.risk_level} testId="result-risk" /></div></div>
            <div><div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-400">Reviewer</div><div className="font-display font-bold text-lg text-[#0F172A] mt-1">{p.reviewer_name}</div></div>
          </div>
        </Card>
        <Card title={null}><div className="flex flex-col items-center justify-center h-full"><div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">Proposal Forensics Score</div><ScoreGauge score={p.proposal_iq_score} size={160} /></div></Card>
        <Card title={null}><div className="flex flex-col items-center justify-center h-full"><div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">Risk Score</div><ScoreGauge score={p.risk_score} size={160} /><div className="text-[10px] text-slate-400 mt-1">higher = safer</div></div></Card>
      </div>

      {/* Fix My Proposal — purchase + rewrite */}
      {!["paid", "proposing", "changes_ready", "changes_failed", "generating", "complete"].includes(p.rewrite_status) && (
        <div className="rounded-lg p-6 bg-black text-white grain flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="fix-cta">
          <div>
            <div className="font-display font-bold text-lg">Proposal Forensics found {issues} issue{issues !== 1 ? "s" : ""} &amp; {opps} optimization{opps !== 1 ? "s" : ""}.</div>
            <div className="text-slate-400 text-sm mt-1">Let Proposal Forensics fix it and generate a client-ready proposal.</div>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <button data-testid="fix-proposal-btn" className="inline-flex items-center gap-2 h-11 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-bold text-sm transition-colors whitespace-nowrap"><Lightning size={17} weight="fill" /> REWRITE MY PROPOSAL</button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Choose your rewrite</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500 -mt-1">Additional charge applies. Secure checkout via Stripe.</p>
              <div data-testid="founding-rewrite-note" className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-700 font-medium">
                Founding Customer Pricing — through Oct 31, 2026. Regular pricing begins Nov 1, 2026.
              </div>
              <div className="space-y-2 mt-2">
                {packages.map((pk) => (
                  <button key={pk.lookup_key} data-testid={`pkg-${pk.lookup_key}`} disabled={buying} onClick={() => startCheckout(pk.lookup_key)}
                    className="w-full flex items-center justify-between p-4 rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors text-left disabled:opacity-60">
                    <span className="text-sm font-semibold text-[#0F172A]">{pk.name}</span>
                    <span className="flex items-baseline gap-2">
                      {pk.regular_price && <span className="text-xs line-through text-slate-400" data-testid={`pkg-regular-${pk.lookup_key}`}>{pk.regular_price}</span>}
                      <span className="font-display font-bold text-[#0F172A]" data-testid={`pkg-price-${pk.lookup_key}`}>{pk.price}</span>
                    </span>
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {p.rewrite_status === "paid" && (
        <div className="rounded-lg p-6 bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="rewrite-paid">
          <div><div className="font-display font-bold text-lg text-[#0F172A]">Rewrite unlocked ✓</div><div className="text-slate-600 text-sm mt-1">First, review the changes Proposal Forensics recommends — you approve what gets applied.</div></div>
          <button data-testid="review-changes-btn" onClick={proposeChanges} className="inline-flex items-center gap-2 h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-bold text-sm transition-colors"><ListChecks size={17} weight="fill" /> Review Proposed Changes</button>
        </div>
      )}
      {p.rewrite_status === "proposing" && (
        <div className="rounded-lg p-6 bg-white border border-slate-200 text-center" data-testid="rewrite-proposing">
          <Sparkle size={26} weight="fill" className="mx-auto text-[#0F172A] animate-pulse" />
          <div className="font-display font-bold text-lg text-[#0F172A] mt-3">Planning your changes…</div>
          <div className="text-slate-500 text-sm mt-1">Proposal Forensics is preparing a list of recommended changes for your approval.</div>
        </div>
      )}
      {p.rewrite_status === "changes_ready" && (p.proposed_changes || []).length > 0 && (() => {
        const approvedCount = Object.values(decisions).filter(Boolean).length;
        return (
          <Card title="Review Proposed Changes" testId="changes-review">
            <p className="text-sm text-slate-600 -mt-2 mb-4">Toggle each change on or off. Only the changes you approve will be applied to your generated proposal.</p>
            <div className="space-y-3">
              {p.proposed_changes.map((c) => {
                const on = !!decisions[c.id];
                return (
                  <div key={c.id} data-testid={`change-item-${c.id}`} className={`rounded-md border p-4 transition-colors ${on ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">{c.category}</span>
                          <span className="text-sm font-semibold text-[#0F172A]">{c.title}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1.5">{c.description}</p>
                        {c.impact && <div className="mt-2 inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">{c.impact}</div>}
                      </div>
                      <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
                        <Switch data-testid={`change-toggle-${c.id}`} checked={on} onCheckedChange={(v) => setDecisions((d) => ({ ...d, [c.id]: v }))} />
                        <span className={`text-[10px] font-bold uppercase ${on ? "text-emerald-600" : "text-slate-400"}`}>{on ? "Apply" : "Skip"}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <div className="text-sm text-slate-500">{approvedCount} of {p.proposed_changes.length} changes selected</div>
              <div className="flex items-center gap-2">
                <button data-testid="select-all-changes-btn" onClick={() => setDecisions(Object.fromEntries(p.proposed_changes.map((c) => [c.id, true])))} className="h-10 px-4 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Select all</button>
                <button data-testid="generate-rewrite-btn" onClick={generateWithDecisions} disabled={approvedCount === 0} className="inline-flex items-center gap-2 h-11 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-bold text-sm transition-colors disabled:opacity-50"><Lightning size={17} weight="fill" /> Generate with {approvedCount} change{approvedCount !== 1 ? "s" : ""}</button>
              </div>
            </div>
          </Card>
        );
      })()}
      {p.rewrite_status === "changes_failed" && (
        <div className="rounded-lg p-6 bg-red-50 border border-red-200 flex items-center justify-between gap-4" data-testid="changes-failed">
          <div className="text-sm text-red-700">Couldn't plan the changes. You've already paid — just retry.</div>
          <button onClick={proposeChanges} className="h-10 px-5 bg-[#0F172A] text-white rounded-md text-sm font-semibold">Retry</button>
        </div>
      )}
      {p.rewrite_status === "generating" && (
        <div className="rounded-lg p-6 bg-white border border-slate-200 text-center" data-testid="rewrite-generating">
          <Sparkle size={26} weight="fill" className="mx-auto text-[#0F172A] animate-pulse" />
          <div className="font-display font-bold text-lg text-[#0F172A] mt-3">Generating your optimized proposal…</div>
          <div className="text-slate-500 text-sm mt-1">Applying your approved changes and your branding. ~1–2 minutes.</div>
        </div>
      )}
      {p.rewrite_status === "failed" && (
        <div className="rounded-lg p-6 bg-red-50 border border-red-200 flex items-center justify-between gap-4">
          <div className="text-sm text-red-700">Rewrite generation failed. You've already paid — just retry.</div>
          <button onClick={generateRewrite} className="h-10 px-5 bg-[#0F172A] text-white rounded-md text-sm font-semibold">Retry</button>
        </div>
      )}
      {p.rewrite_status === "complete" && p.rewrite && (
        <Card title="Proposal Forensics Optimized" testId="rewrite-complete">
          <p className="text-sm text-slate-600 mb-4">{p.rewrite.summary}</p>
          {(p.rewrite.comparison || []).length > 0 && (
            <div className="mb-5">
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase font-mono-plex tracking-widest text-slate-400 border-b border-slate-200 pb-2">
                <span>Metric</span><span>Original</span><span>Proposal Forensics</span>
              </div>
              {p.rewrite.comparison.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 text-sm py-2 border-b border-slate-100">
                  <span className="text-slate-500">{c.metric || "Line item"}</span><span className="text-slate-500 line-through">{c.original}</span><span className="font-semibold text-emerald-700">{c.optimized}</span>
                </div>
              ))}
            </div>
          )}
          {(p.rewrite.change_summary || []).length > 0 && (
            <div className="mb-5">
              <div className="font-display font-bold text-sm text-[#0F172A] mb-2">Changes applied</div>
              <ul className="space-y-1.5">
                {p.rewrite.change_summary.map((c, i) => (
                  <li key={i} className="text-sm text-slate-600 flex gap-2"><CaretRight size={14} weight="bold" className="mt-1 shrink-0 text-blue-500" /><span>{c.category && <span className="font-semibold text-[#0F172A]">{c.category}: </span>}{c.description}</span></li>
                ))}
              </ul>
            </div>
          )}
          <button data-testid="download-rewrite-btn" onClick={downloadRewritePdf} className="inline-flex items-center gap-2 h-11 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-md font-bold text-sm transition-colors"><DownloadSimple size={16} weight="bold" /> Download Client-Ready Proposal</button>
        </Card>
      )}

      <Card title="Category Scores">
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
          {Object.entries(SUBSCORE_LABELS).map(([k, label]) => <Bar key={k} label={label} score={ss[k] ?? 0} />)}
        </div>
      </Card>

      {a.executive_summary && <Card title="Executive Summary"><p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{a.executive_summary}</p></Card>}

      <Card title="Financial Analysis" testId="section-financial">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
          {Object.entries(fin).filter(([k]) => k !== "notes").map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1.5"><span className="text-slate-500">{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span><span className="font-semibold text-[#0F172A] text-right ml-4">{v}</span></div>
          ))}
        </div>
        {(fin.notes || []).length > 0 && <ul className="mt-4 space-y-2">{fin.notes.map((n, i) => <li key={i} className="flex gap-2.5 text-sm text-slate-600"><CaretRight size={14} weight="bold" className="mt-1 shrink-0 text-slate-400" /><span>{n}</span></li>)}</ul>}
      </Card>

      <Card title="Labor Analysis" testId="section-labor">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3"><div className="font-mono-plex text-[10px] uppercase tracking-widest text-slate-400 mb-1">In Proposal</div><div className="text-sm text-slate-700">{lab.proposal_hours || "—"}</div></div>
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3"><div className="font-mono-plex text-[10px] uppercase tracking-widest text-blue-500 mb-1">Proposal Forensics Estimate</div><div className="text-sm text-slate-700">{lab.estimated_hours || "—"}</div></div>
        </div>
        {(lab.findings || []).length > 0 && <ul className="space-y-2">{lab.findings.map((f, i) => <li key={i} className="flex gap-2.5 text-sm text-slate-600"><CaretRight size={14} weight="bold" className="mt-1 shrink-0 text-slate-400" /><span>{f}</span></li>)}</ul>}
      </Card>

      {(a.accuracy_audit || []).length > 0 && (
        <Card title="Accuracy Audit" testId="section-accuracy">
          <div className="space-y-3">
            {a.accuracy_audit.map((it, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-[#0F172A] inline-flex items-center gap-2"><WarningCircle size={15} className="text-amber-500" />{it.item}</span>
                  <div className="flex items-center gap-2"><span className="text-[10px] uppercase font-semibold text-slate-400">{it.type}</span><ClassBadge value={it.classification} /><span className="text-[10px] text-slate-400">{it.confidence}</span></div>
                </div>
                <p className="text-sm text-slate-600 mt-1.5">{it.detail}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ListCard title="Missing Scope" items={a.missing_scope} accent="#D97706" testId="section-missing" />

      {(a.risk_breakdown || []).length > 0 && (
        <Card title="Risk Breakdown" testId="section-risk">
          <div className="space-y-3">
            {a.risk_breakdown.map((r, i) => (
              <div key={i} className="flex gap-3 text-sm"><RiskBadge value={(r.level || "").toUpperCase()} /><div><span className="font-semibold text-[#0F172A]">{r.category}</span><span className="text-slate-600"> — {r.why}</span></div></div>
            ))}
          </div>
        </Card>
      )}

      {a.market_position && (a.market_position.summary || (a.market_position.findings || []).length > 0) && (
        <Card title="Market Position" testId="section-market">
          {a.market_position.summary && <p className="text-sm text-slate-600 mb-4">{a.market_position.summary}</p>}
          {(a.market_position.findings || []).map((f, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 text-sm border-b border-slate-100 py-2">
              <span className="text-slate-500">{f.metric}</span><span className="font-semibold text-[#0F172A]">{f.company_value}</span><span className="text-slate-500">vs {f.benchmark_range}</span><span className="text-right">{f.verdict} <ClassBadge value={f.classification} /></span>
            </div>
          ))}
        </Card>
      )}

      {(a.recommendations || []).length > 0 && (
        <Card title="Recommendations" testId="section-recommendations">
          <div className="space-y-3">
            {a.recommendations.map((r, i) => (
              <div key={i} className="rounded-md bg-slate-50/70 border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-[#0F172A]">{r.title}</span><ClassBadge value={r.classification || "RECOMMENDATION"} /></div>
                <p className="text-sm text-slate-600 mt-1.5">{r.detail}</p>
                {r.impact && <div className="mt-2 inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">{r.impact}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {(a.data_sources || []).length > 0 && (() => {
        const hasLive = a.data_sources.some((d) => (d.citations || []).length > 0);
        return (
        <Card title="Data Sources & Transparency" testId="section-sources">
          <p className="text-xs text-slate-400 mb-3">
            {hasLive
              ? "Regional labor benchmarks below were pulled from live web sources (cited) at analysis time. Product/pricing items without citations are model estimates — verify current figures before sending."
              : "Items below are the basis used, with confidence. Anything requiring current pricing/product data is a model estimate unless marked otherwise — verify against live sources before sending."}
          </p>
          <div className="space-y-2">
            {a.data_sources.map((d, i) => (
              <div key={i} className="border-b border-slate-100 py-2">
                <div className="grid sm:grid-cols-4 gap-2 text-sm">
                  <span className="font-semibold text-[#0F172A] flex items-center gap-1.5">{d.label}{(d.citations || []).length > 0 && <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">Live</span>}</span>
                  <span className="text-slate-500">{d.source}</span>
                  <span className="text-slate-400">{d.date_checked} · {d.applicability}</span>
                  <span className="text-right"><span className="text-[10px] font-bold uppercase text-slate-500">{d.confidence}</span></span>
                </div>
                {(d.citations || []).length > 0 && (
                  <ul className="mt-1.5 pl-1 space-y-1" data-testid={`data-source-citations-${i}`}>
                    {d.citations.map((c, j) => (
                      <li key={j} className="text-xs text-slate-500 flex items-start gap-1.5">
                        <span className="text-slate-300 mt-0.5">•</span>
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{c.title || c.url}</a>
                        {c.date && <span className="text-slate-400 whitespace-nowrap">· {c.date}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-[10px]"><ClassBadge value="FACT" /><ClassBadge value="ESTIMATE" /><ClassBadge value="BENCHMARK" /><ClassBadge value="RECOMMENDATION" /></div>
        </Card>
        );
      })()}

      {a.final_recommendation && <Card title="Final Recommendation"><p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{a.final_recommendation}</p></Card>}
    </div>
  );
}
